<template>
  <div
    v-if="!finished"
    class="flex flex-col h-full"
  >
    <div v-if="showAiNav">
      <AppButton
        v-if="aiIsAvailable"
        class="mr-auto -ml-3 -mt-3 mb-5"
        type="primary"
        text
        @click="$emit('showUpload')"
      >
        <template #icon>
          <FaIcon name="arrow-left" />
        </template>
        {{ hasExtract ? 'Zurück' : 'Zur Vertragsanlage mit KI' }}
      </AppButton>
      <ElAlert
        v-else
        type="info"
        class="mb-5 el-alert--primary"
        show-icon
      >
        <span class="font-medium">KI Vertragsanlage aktuell nicht verfügbar</span>
      </ElAlert>
    </div>
    <div class="flex flex-col gap-4 mb-10">
      <ContractForm
        :model-value="form"
        :validation-errors="errors"
        :update-field="updateField"
      />

      <div class="flex flex-row justify-center md:justify-end md:mr-12 mt-2">
        <AppButton
          type="primary"
          text
          @click="addDocuments = !addDocuments"
        >
          <template #icon>
            <FaIcon :name="addDocuments ? 'chevron-up' : 'chevron-down'" />
          </template>
          Dokument/-e hinzufügen
        </AppButton>
      </div>

      <div v-if="addDocuments" class="mb-5 space-y-4">
        <ElFormItem
          label="Dokument/-e"
          :label-position="isMobile ? 'top' : 'left'"
          class="whitespace-nowrap"
        >
          <div class="w-full flex flex-col items-end">
            <FileInput
              :accept="validFileTypes.join(', ')"
              class="mt-2.5 w-full md:w-80 whitespace-normal"
              @input="setFiles"
            >
              <DocTypesHint />
            </FileInput>

            <InvalidFilesHint
              :files="invalidFiles"
              :error-messages="errorMessages"
            />

            <DocumentList
              :files="validFiles"
              :file-names="fileNames"
              :disable-image-inputs="combine"
              :convert-to-pdf-flags="convertToPdfFlags"
              class="mt-2.5 w-full md:w-80"
              @set-file-name="setFileName"
              @set-convert-to-pdf="setConvertToPdf"
              @remove="remove"
            />

            <ElCheckbox
              v-if="hasMultipleImageFiles"
              id="combine-images-check"
              v-model="combine"
              :disabled="exceedsImageCombinationLimit"
              label="Bilder zu einer PDF-Datei kombinieren"
              class="ml-8 mt-1 mr-auto"
            />

            <ImageCombinationHint
              v-if="exceedsImageCombinationLimit"
              class="ml-1.5 mt-2.5"
            />
          </div>
        </ElFormItem>

        <ElFormItem
          label="Typ"
          for="Typ"
          :label-position="isMobile ? 'top' : 'left'"
        >
          <div class="w-full flex justify-end">
            <ElSelect
              id="Typ"
              v-model="documentType"
              placeholder=""
              clearable
              class="w-full md:w-80"
            >
              <ElOption
                v-for="option in documentTypeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </ElSelect>
          </div>
        </ElFormItem>
      </div>
    </div>

    <div
      v-if="documentsLoading"
      class="text-muted"
    >
      <span class="spinner-border spinner-border-sm mr-1.5" />
      {{ validFiles.length > 1 ? 'Dokumente werden' : 'Dokument wird' }} hochgeladen...
    </div>

    <ElAlert
      v-if="errorMsg"
      :title="errorMsg"
      type="error"
      class="my-5"
      show-icon
    />

    <AppButton
      type="primary"
      class="w-full sm:w-auto sticky bottom-0 mt-auto"
      :handler="submit"
    >
      Vertrag anlegen
    </AppButton>
  </div>

  <div
    v-else
    class="flex flex-col items-center h-full gap-3 pt-5"
  >
    <ElAlert type="success" show-icon>Das Anlegen des Vertrags war erfolgreich.</ElAlert>
    <ElAlert
      v-if="hasUploadError"
      type="success"
      show-icon
    >
      Bei dem Hochladen der Dokumente gab es jedoch einen Fehler.
    </ElAlert>
    <span class="text-sm text-center my-2.5">
      Damit zukünftig die Informationen zu
      diesem Vertrag automatisch übermittelt werden, wird sich Ihr Makler bald nochmal
      bei Ihnen melden.
    </span>
    <AppButton
      class="w-full sticky bottom-0 mt-auto"
      type="primary"
      @click="handleNextContract"
    >
      Weiteren Vertrag anlegen
    </AppButton>
  </div>
</template>

<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { ElAlert } from 'element-plus';
import { ElCheckbox, ElFormItem, ElOption, ElSelect } from 'element-plus';
import { captureException } from '@sentry/vue';
import { ref, watch } from 'vue';

import { createContract } from '@/api/contracts/contractsApi';
import { toOptions } from '@/application/composables/vselect';
import type { ContractData } from '@/contracts/types/types';
import { ContractSource } from '@/contracts/types/types';
import DocTypesHint from '@/upload/components/DocTypesHint.vue';
import FileInput from '@/upload/components/FileInput.vue';
import DocumentList from '@/upload/components/FileList.vue';
import ImageCombinationHint from '@/upload/components/ImageCombinationHint.vue';
import InvalidFilesHint from '@/upload/components/InvalidFilesHint.vue';
import { useContractStore } from '@/contracts/stores/contractStore';
import { TrackEventCategory } from '@/application/utils/tracking/events';
import { checkForDigits, checkForLetters, toUpperCase } from '@/application/utils/strings';
import { useDetectMobile } from "@/application/composables/detectMobile";
import { isSupportedImage, useDocumentUpload } from "@/documents/composables/documentUpload";
import { useDocumentTypes } from "@/documents/composables/documentTypes";
import { useForm } from '@/api/lib/integration';
import ContractForm from '@/contracts/components/ContractForm.vue';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { storeToRefs } from 'pinia';
import { useAiDocumentExtractionStore } from '@/documents/store/aiDocumentExtractionStore.ts';
import { notifyAboutUploadedFiles } from '@/api/documents/documentsApi.ts';
import type { ContractSuggestionData } from '@/api/handlungen/types';
import { intervalMap } from '@/contracts/utils/labelFormatting.ts';

const { showAiNav, contractSuggestionData = undefined } = defineProps<{
  showAiNav?: boolean,
  contractSuggestionData?: ContractSuggestionData,
}>()
const emit = defineEmits(['close', 'finished', 'showUpload']);

const hasUploadError = ref(false);
const finished = ref(false);
const addDocuments = ref(false);
const combine = ref(false);
const documentType = ref<number | null>(null);
const { fetchContractsAndDepots } = useContractStore();
const aiDocumentExtractionStore = useAiDocumentExtractionStore();
const { extract, s3info, hasExtract, aiIsAvailable } = storeToRefs(useAiDocumentExtractionStore());

function getInitialSource(): ContractSource {
  if (hasExtract.value) return ContractSource.AI_EXTRACTION;
  if (contractSuggestionData) return ContractSource.BANKS_API;
  return ContractSource.MANUAL;
}

const { form, submit, updateField, errors, errorMsg, reset  } = useForm<ContractData, { contract_id: number }>(
  createContract,
  {
    sparte_id: extract.value?.sparteId || contractSuggestionData?.sparteId || null,
    gesellschaft_id: extract.value?.gesellschaftId || contractSuggestionData?.gesellschaftId || null,
    vertragsnummer: extract.value?.vertragsnummer || contractSuggestionData?.contractNumber || null,
    beginn: extract.value?.beginn || null,
    ablaufdatum: extract.value?.ablauf || null,
    beitrag: extract.value?.beitrag || contractSuggestionData?.paymentAmount || null,
    zahlweise: extract.value?.zahlweise || ( contractSuggestionData ? intervalMap.get(contractSuggestionData.paymentInterval) as number : null),
    kennzeichen: {
      unterscheidungszeichen: null,
      erkennungszeichen: null,
      erkennungsnummer: null,
      abschliessendeszeichen: null,
    },
    notiz: null,
    source: getInitialSource(),
  },
  async (data) => {
    await uploadDocumentsForContract(data.contract_id);
    await notifyAboutUploadedAiExtractionFiles(data.contract_id);
    const differences = await aiDocumentExtractionStore.compareExtractionWithForm(form.value);
    useTagManager().push({ event: TrackEventCategory.CONTRACT, differences })
    useTagManager().push({ event: hasExtract.value ? TrackEventCategory.AI_CREATE_CONTRACT : TrackEventCategory.CREATE_CONTRACT })
    aiDocumentExtractionStore.$reset();
    await fetchContractsAndDepots(true);
    finished.value = true;
    emit('finished');
    clear();
  },
);

watch(hasExtract, (value) => {
  if (value) {
    form.value.source = ContractSource.AI_EXTRACTION;
  }
});

const {
  validFileTypes,
  validFiles,
  invalidFiles,
  fileNames,
  convertToPdfFlags,
  setConvertToPdf,
  hasMultipleImageFiles,
  exceedsImageCombinationLimit,
  errorMessages,
  setFiles,
  setFileName,
  remove,
  reset: resetUpload,
  upload,
  loading: documentsLoading,
} = useDocumentUpload({ compressImages: true });
const { documentTypes, fetchDocumentTypes } = useDocumentTypes();
const { isMobile } = useDetectMobile();

const documentTypeOptions = toOptions(documentTypes, 'name', 'id');

watch(() => form.value.kennzeichen.unterscheidungszeichen, (value) => {
  form.value.kennzeichen.unterscheidungszeichen = toUpperCase(checkForLetters(value));
}, { flush: 'post' });

watch(() => form.value.kennzeichen.erkennungszeichen, (value) => {
  form.value.kennzeichen.erkennungszeichen = toUpperCase(checkForLetters(value));
}, { flush: 'post' });

watch(() => form.value.kennzeichen.erkennungsnummer, (value, oldValue) => {
  if (!value) return;
  const output = checkForDigits(value.toString());
  if (output !== null) {
    form.value.kennzeichen.erkennungsnummer = Number.parseInt(output, 10);
  } else if (oldValue !== null && Number.isInteger(oldValue)) {
    form.value.kennzeichen.erkennungsnummer = oldValue;
  } else {
    form.value.kennzeichen.erkennungsnummer = null;
  }
}, { flush: 'post' });

watch([hasMultipleImageFiles, exceedsImageCombinationLimit], () => {
  combine.value = hasMultipleImageFiles.value && !exceedsImageCombinationLimit.value;
});

watch(
  combine,
  () => validFiles.value.forEach((file, index) => {
    if (isSupportedImage(file)) {
      setFileName(file.name, index);
    }
  }),
);

const clear = () => {
  // contract related
  reset();
  // upload related
  documentType.value = null;
  addDocuments.value = false;
  resetUpload();
};

function handleNextContract() {
  finished.value = false;
  aiDocumentExtractionStore.$reset();
  emit('showUpload');
}

const uploadDocumentsForContract = async (contractId: number) => {
  hasUploadError.value = false;

  if (!(addDocuments.value && validFiles.value.length > 0)) return;

  try {
    await upload({
      contract_id: contractId,
      insurance_company_id: form.value.gesellschaft_id,
      product_combo_id: form.value.sparte_id,
      document_type_id: documentType.value,
      note: null,
      combine: combine.value,
    });
  } catch (error) {
    // We don't want an errorneous upload to fail the contract creation process
    // so we pass here. When an upload fails we render a message to the user
    hasUploadError.value = true;
    captureException(error);
  }
  useTagManager().push({ event: TrackEventCategory.DOCUMENT_UPLOAD, action: 'contract' })
};

const notifyAboutUploadedAiExtractionFiles = async (contractId: number) => {
  if (s3info.value.length === 0) return;

  try {
    await notifyAboutUploadedFiles({
      contract_id: contractId,
      insurance_company_id: null,
      product_combo_id: null,
      document_type_id: 60, // 60 => Versicherungsschein / Police
      note: null,
      combine: false,
      uploads: s3info.value,
    })
  } catch (error) {
    // We don't want an errorneous upload to fail the contract creation process
    // so we pass here. When an upload fails, we render a message to the user
    hasUploadError.value = true;
    captureException(error);
  }
}

fetchDocumentTypes();
</script>
